import { OnInit, Directive, Input, AfterContentInit, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { AppBarTitleService, ApplicationOfferBundleData, ApplicationCheckoutRequirement, ApplicationBaseData, ProductCode, ApplicationOffer, OfferCalculator, OfferLocCalculator, OfferTermCalculator, OfferLawCalculator, OfferCalculatorService, ShowDisclosuresData, isLargeLoanProgramOffer, LargeLoanProgramCommissionRate } from 'common';
import * as _ from 'lodash';
import { AppSettings } from '../../../app.settings';
import { CurrencyPipe, PercentPipe } from '@angular/common';
import { ApplicationService } from '../../shared/application.service';
import { OfferBundleData } from '../../shared/offer-bundle.service';
import { OfferNavigator } from '../offer-navigator';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { offerPageScreenFor, OfferPageScreen } from '../offer-page-status/offer-page-status.model';

@UntilDestroy()
@Directive()
export abstract class BaseCalculatorComponent implements OnInit, AfterContentInit, OnChanges {

    @Input() offer: ApplicationOffer;
    @Input() customerView: boolean

    offerCalculator: OfferCalculator;
    navigator: OfferNavigator;

    readonly OfferPageScreen = OfferPageScreen;

    // Left undefined until the application response lands, so the template renders neither the
    // offer body nor a message screen during the round-trip.
    offerPageScreen: OfferPageScreen;

    private stageScreen: OfferPageScreen;
    private routeDataResolved = false;

    protected uuid: string;
    offerId: number;
    advisorId: number;
    brandingCss: SafeResourceUrl;
    offerDetails: ApplicationOffer[] = [];
    application: ApplicationBaseData;
    bundle: ApplicationOfferBundleData;
    checkoutRequirements: ApplicationCheckoutRequirement[];
    offerBundles: OfferBundleData[];
    isRenewal: boolean;

    constructor(
        protected _route: ActivatedRoute,
        private _sanitizer: DomSanitizer,
        protected _appBarTitleService: AppBarTitleService,
        protected _calculatorService: OfferCalculatorService,
        private applicationService: ApplicationService,
        protected settings: AppSettings,
        private percentPipe: PercentPipe,
        private currencyPipe: CurrencyPipe
    ) {
        
    }

    ngOnInit() {
        this._route.data.pipe(untilDestroyed(this)).subscribe(this.dataInit.bind(this));
        this._route.queryParamMap.pipe(untilDestroyed(this)).subscribe(params => {

            let subdomain = null;

            if (!environment.production)
                subdomain = params.get("subdomain");

            if (!subdomain)
                // eslint-disable-next-line max-len
                this.brandingCss = this._sanitizer.bypassSecurityTrustResourceUrl(`${this.settings.brokers.url}/api/branding/loc-calculator-css`);
            else
                // eslint-disable-next-line max-len
                this.brandingCss = this._sanitizer.bypassSecurityTrustResourceUrl(`${this.settings.brokers.url}/api/branding/loc-calculator-css?subdomain=${subdomain}`);

            this.uuid = params.get("uuid");

            this.applicationService.getByUUID(this.uuid)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: this.applicationDataInit.bind(this),
                error: error => {
                    console.error(error);
                    // Fail safe: the processing screen never reveals an offer.
                    this.stageScreen = OfferPageScreen.Processing;
                    this.setOfferPageScreen(OfferPageScreen.Processing);
                }
            });
        });
    }

    applicationDataInit(data: ApplicationBaseData) {
        this.stageScreen = offerPageScreenFor(data?.stage);
        this.resolveOfferPageScreen();
        this.application = data;
        this.isRenewal = data?.renewalForLoanUniqueId ? true : false;
        this.navigator = new OfferNavigator(data?.offerBundles, this.bundle?.id);
        if (this.offerCalculator) {
            this.offerCalculator.application = data;
            const isLargeLoanProgram = isLargeLoanProgramOffer(this.offerCalculator.applicationOfferBundle);
            this.offerCalculator.brokerInitialDrawCommission =
                isLargeLoanProgram ? LargeLoanProgramCommissionRate : data?.brokerInitialDrawCommission / 100;
        }
    }

    dataInit(data: any) {
        this.customerView = data.preview;
        this.bundle = data.bundle;
        this.routeDataResolved = true;
        this._calculatorService.bundleChanged.next(this.bundle);
        this.resolveOfferPageScreen();
    }

    ngOnChanges(_changes: SimpleChanges): void {
        this.ngAfterContentInit();
    }

    ngAfterContentInit(): void {
        this.offerCalculator = this.createOfferCalculator();
        this.checkoutRequirements = this.bundle?.checkoutRequirements;
    }

    createOfferCalculator(): OfferCalculator {
        switch (this.offer?.productCode) {
            case ProductCode.LOC: return new OfferLocCalculator(this.application, this.offer, this.bundle, this.percentPipe, this.currencyPipe, this._calculatorService);
            case ProductCode.Term: return new OfferTermCalculator(this.application, this.offer, this.bundle, this.percentPipe, this.currencyPipe, this._calculatorService);
            case ProductCode.Law: return new OfferLawCalculator(this.application, this.offer, this.bundle, this.percentPipe, this.currencyPipe, this._calculatorService);
            default: return null;
        }
    }

    // A message screen replaces the product view entirely, so it gets the generic title instead of
    // the route's own. Routed through here rather than assigned directly because ngAfterContentInit
    // and the application response are unordered — either may run last.
    protected setAppBarTitle(fullOfferTitle: string): void {
        this._appBarTitleService.title =
            this.offerPageScreen && this.offerPageScreen !== OfferPageScreen.FullOffer ? 'Offers' : fullOfferTitle;
    }

    // A full offer needs a bundle to render. A bundle that failed to resolve is the same class of
    // failure as an application that failed to load, so it takes the same fail-safe rather than
    // rendering a calculator with no offer behind it.
    private resolveOfferPageScreen(): void {
        if (!this.stageScreen)
            return;

        const missingBundle = this.stageScreen === OfferPageScreen.FullOffer && this.routeDataResolved && !this.bundle;

        this.setOfferPageScreen(missingBundle ? OfferPageScreen.Processing : this.stageScreen);
    }

    private setOfferPageScreen(screen: OfferPageScreen): void {
        this.offerPageScreen = screen;

        if (screen !== OfferPageScreen.FullOffer)
            this._appBarTitleService.title = 'Offers';
    }

    onShowDisclosures(data: ShowDisclosuresData): void {
        window.open(`/disclosures?uuid=${data.applicationUuid}&offerId=${data.offerId}`, "_blank");
    }
}
